import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import { useSelector } from 'react-redux';
import ToggleButton from '@mui/material/ToggleButton';
import { lecturesSelectors } from '../../../slices/lectures/lecturesSlice';
import moment from 'moment';
import OraSwitch from '../../CustomMUI/Switch';
import { Check, Eye, Info, Plus } from 'react-feather';
import { RootState } from '../../../store/store';
import { useEffect, useState } from 'react';
import { Lecture } from '../../../types/Lecture';
import { hapticsImpactLight } from '../../../utils/haptics';
import { Link, useNavigate } from 'react-router-dom';
import { getExamLectures, updateExamLecture } from '../../../services/exams';
import { useUserExamUpdate } from '../../../utils/updateUserExam';
import LectureEditorModal from '../../ExamEditor/LectureEditorModal';
import { fetchTodayTopicsIfNeeded } from '../../../services/study';
import { selectTodaySession } from '../../../slices/todaySession/todaySessionSlice';
import {
  selectTodayTopics,
  selectTodayTopicsStatus,
} from '../../../slices/todayTopics/todayTopicsSlice';
import { Topic } from '../../../types/Dashboard';
import { getYieldFilter } from '../../../utils/examUtils';

interface LecturePanelProps {
  isEditingCustomSession: boolean;
}

const LecturePanel: React.FC<LecturePanelProps> = ({
  isEditingCustomSession,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const todaySession = useSelector(selectTodaySession);
  const lectures = useSelector(lecturesSelectors.selectAll);

  // Replace local state with Redux selectors
  const todayTopics = useSelector(selectTodayTopics);
  const todayTopicsStatus = useSelector(selectTodayTopicsStatus);

  const { updateExam } = useUserExamUpdate(undefined, {
    showSuccessMessage: false,
  });

  const [isLectureEditorModalOpen, setIsLectureEditorModalOpen] =
    useState(false);
  const [editingLecture, setEditingLecture] = useState<Lecture | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (
      primaryExam &&
      primaryExam.type !== 'CUSTOM' &&
      todaySession &&
      todaySession.date
    ) {
      fetchTodayTopicsIfNeeded(todaySession.date);
    }
  }, [todaySession, primaryExam, todayTopicsStatus]);

  useEffect(() => {
    if (primaryExam && primaryExam.type === 'CUSTOM') {
      getExamLectures(primaryExam.id);
    }
  }, [primaryExam]);

  const renderLectureModeToggle = () => {
    if (primaryExam?.type !== 'CUSTOM') {
      return null;
    }
    return (
      <ToggleButtonGroup
        value={primaryExam?.lectureMode}
        exclusive
        onChange={handleToggleLectureAutopilot}
        aria-label="Lecture Mode"
        disabled={isEditingCustomSession}
      >
        <Tooltip
          title="Let Ora determine today's session based on your lecture dates, exam date, and performance."
          enterTouchDelay={0}
        >
          <ToggleButton
            value="AUTOPILOT"
            aria-label="Autopilot"
            tabIndex={0}
            disableRipple
          >
            Autopilot
            <svg
              width="6"
              height="6"
              viewBox="0 0 6 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 6C6 2.68629 3.31371 0 0 0H6V6Z"
                fill="var(--home-inactive-tab-background)"
              />
            </svg>
          </ToggleButton>
        </Tooltip>
        <Tooltip
          title="Choose the specific lectures that are included in today's session."
          enterTouchDelay={0}
        >
          <ToggleButton
            value="MANUAL"
            aria-label="Manual"
            tabIndex={0}
            disableRipple
          >
            Manual
            <svg
              width="6"
              height="6"
              viewBox="0 0 6 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 6C0 2.68629 2.68629 0 6 0H0V6Z"
                fill="var(--home-inactive-tab-background)"
              />
            </svg>
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    );
  };

  const handleToggleLectureAutopilot = async () => {
    if (!currentUser?.id || !primaryExam?.id) {
      return;
    }
    await updateExam({
      userId: currentUser.id,
      examId: primaryExam.id,
      lectureMode:
        primaryExam.lectureMode === 'AUTOPILOT' ? 'MANUAL' : 'AUTOPILOT',
    });
  };

  const handleToggleLectureEnabled = async (lecture: Lecture) => {
    if (!primaryExam?.id) {
      return;
    }
    await updateExamLecture(primaryExam.id, lecture.id, {
      enabled: !lecture.enabled,
    });
  };

  const renderLectures = () => {
    if (primaryExam && lectures.length > 0) {
      return Object.entries(
        lectures.reduce(
          (acc, lecture) => {
            const date = lecture.date
              ? moment(lecture.date).format('MMMM D')
              : 'No Date';
            if (!acc[date]) {
              acc[date] = [];
            }
            acc[date].push(lecture);
            return acc;
          },
          {} as Record<string, typeof lectures>
        )
      ).map(([date, dateLectures]) => (
        <div key={date} className="lectures-topics_list_date-group">
          <div className="lectures-topics_list_date-group_label">{date}</div>
          {dateLectures.map((lecture) => (
            <div
              className="lectures-topics_list_date-group_item"
              key={lecture.id}
            >
              {primaryExam.lectureMode === 'MANUAL' ? (
                <OraSwitch
                  checked={lecture.enabled}
                  onChange={() => {
                    handleToggleLectureEnabled(lecture);
                  }}
                />
              ) : lecture.enabled ? (
                <Check />
              ) : null}
              <div
                className="lectures-topics_list_date-group_item_title"
                onClick={() => {
                  setIsLectureEditorModalOpen(true);
                  setEditingLecture(lecture);
                }}
              >
                <h4>{lecture.name}</h4>
                <Eye />
              </div>
            </div>
          ))}
        </div>
      ));
    } else {
      return (
        <div className="lectures-topics_list_empty">
          <button
            className="button button--add"
            onClick={() => {
              navigate('/modify-exam');
              hapticsImpactLight();
            }}
          >
            <div className="button--add_icon">
              <Plus />
            </div>
            Add Lectures
          </button>
        </div>
      );
    }
  };

  const renderYieldGauge = (topic: Topic) => {
    if (!primaryExam) return null;
    const yieldKey = getYieldFilter(primaryExam.id);
    const tYield = topic[yieldKey as keyof Topic] as number | undefined;
    return (
      <Tooltip title={'Topic Yield'} enterTouchDelay={0}>
        <div className="topic-yield-gauge">
          <div className="topic-yield-gauge_number">{tYield}</div>
          <div className="topic-yield-gauge_display">
            <div
              className={`topic-yield-gauge_display_level ${tYield && tYield > 20 ? 'topic-yield-gauge_display_level--fill' : ''}`}
            />
            <div
              className={`topic-yield-gauge_display_level ${tYield && tYield > 40 ? 'topic-yield-gauge_display_level--fill' : ''}`}
            />
            <div
              className={`topic-yield-gauge_display_level ${tYield && tYield > 60 ? 'topic-yield-gauge_display_level--fill' : ''}`}
            />
            <div
              className={`topic-yield-gauge_display_level ${tYield && tYield > 80 ? 'topic-yield-gauge_display_level--fill' : ''}`}
            />
          </div>
        </div>
      </Tooltip>
    );
  };

  const renderTopics = () => {
    return (
      <>
        <div className="lectures-topics_header">
          <div className="lectures-topics_header_title">
            Topics in Today's Session
          </div>
          <div className="lectures-topics_header_subtitle">
            Determined by performance and yield
          </div>
        </div>
        <div className="topic-list">
          {todayTopics.map((topic) => (
            <div className="topic" key={topic.id}>
              <Link
                key={topic.id}
                className="topic_name"
                to={`/library/${topic.articleId}`}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  hapticsImpactLight();
                }}
                aria-label={`View ${topic.name}`}
                style={{
                  textDecoration: 'none',
                }}
              >
                {topic.name}
              </Link>
              <div className="topic_info">{renderYieldGauge(topic)}</div>
            </div>
          ))}
        </div>
      </>
    );
  };

  const renderList = () => {
    if (primaryExam?.type === 'CUSTOM') {
      return (
        <>
          <div className="lectures-topics_header">
            <div className="lectures-topics_header_title">
              Lectures in Today's Session
            </div>
            {primaryExam.lectureMode === 'AUTOPILOT' ? (
              <div className="lectures-topics_header_subtitle">
                Auto-enabled by default
                <Tooltip
                  title="Lecture content will automatically be enabled on the day of each lecture."
                  enterTouchDelay={0}
                >
                  <Info />
                </Tooltip>
              </div>
            ) : (
              <div className="lectures-topics_header_subtitle">
                Choose which lectures to study
                <Info style={{ opacity: 0 }} />
              </div>
            )}
          </div>
          {renderLectures()}
          {isLectureEditorModalOpen && (
            <LectureEditorModal
              readOnlyMode={true}
              handleClose={() => {
                setEditingLecture(null);
                setIsLectureEditorModalOpen(false);
              }}
              handleLectureSave={() => {}}
              editLecture={editingLecture || undefined}
              handleMapFile={() => {}}
            />
          )}
        </>
      );
    } else {
      return <>{renderTopics()}</>;
    }
  };

  return (
    <>
      {!primaryExam?.isRCT && (
        <div
          className="lectures-topics"
          style={
            isEditingCustomSession
              ? {
                  opacity: 0.7,
                  pointerEvents: 'none',
                }
              : {}
          }
        >
          <div className="lectures-topics_toggle">
            {renderLectureModeToggle()}
          </div>
          <div className="mask-wrap">
            <div className="lectures-topics_list">{renderList()}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default LecturePanel;
